import { Button, Modal, Row, SearchBar } from "app/components";
import { boxShadow, colors } from "app/utils/theme";
import { openPageInNewTab, safeLower } from "app/utils/utils";
import { rApp, rApps, rConfirmationModalData, rPages } from "app/utils/recoil";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { AdminForm } from "../components";
import AdminWrapper from "./AdminWrapper";
import CardGrid from "../components/CardGrid";
import Cookies from "js-cookie";
import { EmptyStateDashboard } from "app/renderPage/EmptyStates";
import NewPageModal from "../pages/NewPageModal";
import { apiRequest } from "app/utils/apiRequests";
import { get } from "lodash";
import mixpanel from "mixpanel-browser";
import styled from "styled-components";
import { successNotification } from "app/utils/Notification";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

export const PagesContent = ({ itemsPerPage = 12 }) => {
  const [pages, setPages] = useRecoilState(rPages);

  const [copyPageId, setCopyPageId] = useState(null);
  const [copyPageNewAppId, setCopyPageNewAppId] = useState(null);

  const apps = useRecoilValue(rApps);

  const app = useRecoilValue(rApp);

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showNewPageModal, setShowNewPageModal] = useState(false);

  const setConfirmationModalData = useSetRecoilState(rConfirmationModalData);

  const deletePage = (pageId) => {
    const newPages = pages.filter((p) => p.id !== pageId);
    setPages(newPages);
    successNotification("Page Deleted");
    mixpanel.track("Delete Page");

    apiRequest.delete("/page/", {
      data: { id: pageId },
    });
  };

  const filteredPages = pages.filter(
    (p) =>
      search === "" ||
      safeLower(p.name).includes(safeLower(search)) ||
      safeLower(p.route).includes(safeLower(search))
  );

  const copyPageToApp = () => {
    setConfirmationModalData({
      title: `Copy Page into the ${get(
        apps.find((a) => a.id === copyPageNewAppId),
        "name"
      )} app?`,
      text: "Note: Pages with Reusable or Repeating custom blocks are not supported. You can still copy these pages but those blocks will not be copied.",
      confirm: () => {
        apiRequest
          .post("/copy_page/", {
            id: copyPageId,
            new_app_id: copyPageNewAppId,
          })
          .then(() => {
            successNotification("Page Copied");
            setCopyPageId(null);
            setCopyPageNewAppId(null);
          });
      },
    });
  };

  return (
    <div>
      {copyPageId && (
        <Modal
          hide={() => {
            setCopyPageId(null);
            setCopyPageNewAppId(null);
          }}
          minWidth={"350px"}
          header={{ title: "Copy Page To Another App" }}
        >
          <AdminForm
            sectionPadding={"0px"}
            submitText={"Copy Page"}
            submit={copyPageToApp}
            disableSubmit={!copyPageNewAppId}
            onChange={(k, v) => {
              if (k === "app") {
                setCopyPageNewAppId(v);
              }
            }}
            fields={[
              {
                label: "App",
                id: "app",
                componentId: "Select",
                hideEmptyItem: true,
                value: copyPageNewAppId,
                options: apps
                  .filter((a) => a.id !== app.id)
                  .map((a) => ({ label: a.name, value: a.id })),
              },
            ]}
          />
        </Modal>
      )}
      {showNewPageModal && (
        <NewPageModal hide={() => setShowNewPageModal(false)} />
      )}
      <Row
        justifyContent="space-between"
        alignItems="center"
        margin="0 0 30px 0"
      >
        <Label>Pages</Label>
        <Row alignItems="center" gap="15px">
          <SearchBar
            data={{
              value: search,
              placeholder: "Search pages",
              onChange: (v) => setSearch(v),
            }}
          />
          <Button
            data={{
              text: "New Page",
              icon: "FiPlus",
              onClick: () => setShowNewPageModal(true),
            }}
          />
        </Row>
      </Row>

      {pages.length === 0 && (
        <EmptyStateDashboard
          title="You don't have any pages"
          subtitle="Pages are the basic building blocks of your app, where you can show content. Get started by clicking the button below."
          graphic={
            "https://res.cloudinary.com/frontly/image/upload/v1716244997/Create_ljrvzs.svg"
          }
          buttonText="New Page"
          onClick={() => setShowNewPageModal(true)}
          secondaryButton={{
            text: "Browse Pages Marketplace",
            icon: "FiExternalLink",
            onClick: () => navigate("/marketplace?type=pages"),
          }}
        />
      )}

      {pages.length > 0 && (
        <CardGrid
          noResultsName={"pages"}
          items={filteredPages.map((p) => ({
            title: p.name,
            description: p.route,
            onClick: () => navigate(`/page/${p.id}`),
            badges: [
              {
                color: p.published ? "#b8e7d2" : "#AFE6F1",
                text: p.published ? "Published" : "Draft",
              },
            ],
            options: [
              {
                label: "View Live Page",
                icon: "FiExternalLink",
                onClick: () => {
                  const token = Cookies.get("accessToken");
                  openPageInNewTab({ page: p, app, token });
                },
              },
              {
                label: "Duplicate Page",
                icon: "FiCopy",
                onClick: () => {
                  apiRequest
                    .post("/duplicate_page/", { id: p.id })
                    .then((r) => {
                      const newPage = get(r, "data");
                      setPages([...pages, newPage]);
                      successNotification("Page Duplicated");
                    });
                },
              },
              {
                label: "Copy To Another App",
                icon: "FiCopy",
                onClick: () => setCopyPageId(p.id),
              },
              {
                label: "Delete",
                icon: "FiTrash",
                onClick: () =>
                  setConfirmationModalData({
                    title: "Delete Page",
                    text: "Are you sure you want to delete this page? This is irreversible.",
                    confirm: () => deletePage(p.id),
                  }),
              },
            ],
          }))}
          itemsPerPage={itemsPerPage}
        />
      )}
    </div>
  );
};

const Pages = () => {
  return (
    <AdminWrapper>
      <PagesContent />
    </AdminWrapper>
  );
};

export default Pages;

export const Label = styled.div`
  font-size: 24px;
  font-weight: 600;
`;

export const Container = styled.div`
  border-radius: 10px;
  box-shadow: ${boxShadow.card};
  background: white;
  overflow: hidden;
  margin-top: 20px;
`;

export const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;

export const Header = styled.div`
  padding: 15px;
`;

export const Badge = styled.div`
  background: ${(p) => p.color};
  padding: 5px 7px 5px 7px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 400;
  color: #202223;
  width: fit-content;
`;

export const CardLabel = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

export const CardText = styled.div`
  font-size: 14px;
  font-weight: 300;
  color: var(--grey7);
  margin-top: 4px;
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  padding: 15px;
  cursor: pointer;
  gap: 20px;
  box-shadow: var(--card-shadow);
  border-radius: 12px;
  &:hover {
    background: var(--grey1);
  }
`;

export const CardSection = styled.div`
  display: flex;
  background: white;
  padding: 20px;
  gap: 25px;
  border-radius: 10px;
  box-shadow: var(--card-shadow);
  overflow: auto;
`;
